<template>
  <Teleport to="body">
    <!-- Offline Banner -->
    <Transition name="offline-slide">
      <div v-if="showOfflineBanner" class="offline-banner">
        <div class="offline-content">
          <v-icon class="offline-icon">mdi-wifi-off</v-icon>
          <div class="offline-text">
            <span class="offline-title">You're offline</span>
            <span class="offline-subtitle">Some features may not be available</span>
          </div>
          <v-btn 
            v-if="isOnline && hasFailedQueries"
            size="small" 
            variant="text" 
            color="white"
            @click="retryFailedQueries"
            :loading="isRetrying"
            class="offline-retry-btn"
          >
            Retry
          </v-btn>
          <v-btn 
            size="small" 
            variant="text" 
            icon="mdi-close"
            color="white"
            @click="dismissBanner"
            class="offline-dismiss-btn"
          />
        </div>
      </div>
    </Transition>

    <!-- Error Snackbar -->
    <v-snackbar
      v-model="showErrorSnackbar"
      :timeout="errorTimeout"
      :color="errorColor"
      location="bottom"
      class="error-snackbar"
    >
      <div class="error-snackbar-content">
        <v-icon class="error-snackbar-icon">{{ errorIcon }}</v-icon>
        <span class="error-snackbar-text">{{ errorMessage }}</span>
      </div>
      
      <template v-slot:actions>
        <v-btn
          v-if="canRetry"
          variant="text"
          size="small"
          @click="retryLastAction"
          :loading="isRetrying"
        >
          Retry
        </v-btn>
        <v-btn
          variant="text"
          icon="mdi-close"
          size="small"
          @click="showErrorSnackbar = false"
        />
      </template>
    </v-snackbar>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useErrorHandling } from '@/composables/useErrorHandling'

const queryClient = useQueryClient()
const { 
  isOffline, 
  isOnline, 
  globalError, 
  hasNetworkError,
  hasAuthError,
  clearError 
} = useErrorHandling()

// Banner state
const showOfflineBanner = ref(false)
const bannerDismissed = ref(false)
const isRetrying = ref(false)

// Error snackbar state
const showErrorSnackbar = ref(false)
const lastRetryAction = ref<(() => Promise<void>) | null>(null)

// Computed properties
const hasFailedQueries = computed(() => {
  const cache = queryClient.getQueryCache()
  return cache.getAll().some(query => query.state.status === 'error')
})

const errorMessage = computed(() => globalError.value?.message || '')
const canRetry = computed(() => globalError.value?.retryable || false)

const errorColor = computed(() => {
  if (!globalError.value) return 'error'
  
  switch (globalError.value.type) {
    case 'network':
      return isOffline.value ? 'warning' : 'error'
    case 'auth':
      return 'error'
    case 'validation':
      return 'warning'
    default:
      return 'error'
  }
})

const errorIcon = computed(() => {
  if (!globalError.value) return 'mdi-alert-circle'
  
  switch (globalError.value.type) {
    case 'network':
      return isOffline.value ? 'mdi-wifi-off' : 'mdi-web-off'
    case 'auth':
      return 'mdi-account-alert'
    case 'validation':
      return 'mdi-alert'
    default:
      return 'mdi-alert-circle'
  }
})

const errorTimeout = computed(() => {
  if (!globalError.value) return 5000
  
  switch (globalError.value.type) {
    case 'auth':
      return 10000 // Keep auth errors visible longer
    case 'network':
      return isOffline.value ? -1 : 5000 // Keep offline errors persistent
    case 'validation':
      return 7000
    default:
      return 5000
  }
})

// Watch for offline status changes
watch(isOffline, (offline) => {
  if (offline && !bannerDismissed.value) {
    showOfflineBanner.value = true
  } else if (!offline) {
    showOfflineBanner.value = false
    bannerDismissed.value = false
    
    // Auto-retry failed queries when coming back online
    if (hasFailedQueries.value) {
      retryFailedQueries()
    }
  }
})

// Watch for global errors
watch(globalError, (error) => {
  if (error) {
    if (error.type === 'network' && isOffline.value) {
      // Show banner instead of snackbar for offline errors
      if (!bannerDismissed.value) {
        showOfflineBanner.value = true
      }
    } else {
      // Show snackbar for other errors
      showErrorSnackbar.value = true
    }
  }
})

// Actions
const retryFailedQueries = async () => {
  if (!isOnline.value) return
  
  isRetrying.value = true
  
  try {
    await queryClient.refetchQueries({
      predicate: (query) => query.state.status === 'error'
    })
    console.log('Retried failed queries successfully')
  } catch (error) {
    console.error('Failed to retry queries:', error)
  } finally {
    isRetrying.value = false
  }
}

const retryLastAction = async () => {
  if (!lastRetryAction.value || !canRetry.value) return
  
  isRetrying.value = true
  
  try {
    await lastRetryAction.value()
    clearError()
    showErrorSnackbar.value = false
  } catch (error) {
    console.error('Retry failed:', error)
  } finally {
    isRetrying.value = false
  }
}

const dismissBanner = () => {
  bannerDismissed.value = true
  showOfflineBanner.value = false
}

// Set up automatic retry on reconnection
let retryTimeout: ReturnType<typeof setTimeout> | null = null

const scheduleRetry = () => {
  if (retryTimeout) {
    clearTimeout(retryTimeout)
  }
  
  retryTimeout = setTimeout(() => {
    if (isOnline.value && hasFailedQueries.value) {
      retryFailedQueries()
    }
  }, 2000) // Wait 2 seconds after coming online to retry
}

// Listen for online events
onMounted(() => {
  const handleOnline = () => {
    console.log('Back online - scheduling retry of failed queries')
    scheduleRetry()
  }
  
  window.addEventListener('online', handleOnline)
  
  onUnmounted(() => {
    window.removeEventListener('online', handleOnline)
    if (retryTimeout) {
      clearTimeout(retryTimeout)
    }
  })
})

// Store retry action for snackbar
const setRetryAction = (action: () => Promise<void>) => {
  lastRetryAction.value = action
}

// Expose retry action setter for other components
defineExpose({
  setRetryAction
})
</script>

<style scoped>
.offline-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, #ff6b35 0%, #f7931e 100%);
  color: white;
  z-index: 9999;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.offline-content {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-3) var(--space-4);
  max-width: 1200px;
  margin: 0 auto;
  gap: var(--space-3);
}

.offline-icon {
  font-size: 20px;
  flex-shrink: 0;
}

.offline-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  text-align: center;
}

.offline-title {
  font-weight: 600;
  font-size: 14px;
}

.offline-subtitle {
  font-size: 12px;
  opacity: 0.9;
}

.offline-retry-btn,
.offline-dismiss-btn {
  flex-shrink: 0;
}

.offline-slide-enter-active,
.offline-slide-leave-active {
  transition: transform 0.3s ease-out;
}

.offline-slide-enter-from {
  transform: translateY(-100%);
}

.offline-slide-leave-to {
  transform: translateY(-100%);
}

.error-snackbar {
  z-index: 9998;
}

.error-snackbar-content {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.error-snackbar-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.error-snackbar-text {
  flex: 1;
  font-size: 14px;
}

/* Mobile responsive */
@media (max-width: 600px) {
  .offline-content {
    padding: var(--space-2) var(--space-3);
  }
  
  .offline-text {
    text-align: left;
  }
  
  .offline-subtitle {
    display: none; /* Hide subtitle on mobile to save space */
  }
}
</style>
